import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR as dateFnsPtBR, enUS, es } from "date-fns/locale";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalAxios } from "../../../../axios/axios-config";
import TableList from "../../../../components/TableList/TableList";
import { INfeTotvsLogData } from "../../../../models/nfe-totvs-log";

const NFE_TOTVS_LOG_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/ForubusNfeTotvsLog/ForubusNfeTotvsLog";

interface INfeTotvsDetailsModalProps {
  nfeData: INfeTotvsLogData;
  onClose: () => void;
}

const translationPath = "page.4uBus.logs.nfeTotvs.details.";

const NfeTotvsDetailsModal: React.FC<INfeTotvsDetailsModalProps> = ({
  nfeData,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const [logData, setLogData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showRawLog, setShowRawLog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await GlobalAxios.get(
          `${NFE_TOTVS_LOG_ROUTE}?ForubusNfeTotvsIdentifier=${nfeData.identifier}`,
        );

        if (response.data?.items) {
          setLogData(
            response.data.items.map((item: any, index: number) => ({
              ...item,
              id: index + 1,
            })),
          );
        }
      } catch (error) {
        console.error("Erro ao buscar logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [nfeData.identifier]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return format(date, "Pp", {
      locale:
        i18n.language === "pt-BR"
          ? dateFnsPtBR
          : i18n.language === "en-US"
            ? enUS
            : es,
    }).replaceAll(",", "");
  };

  const handleCloseLogModal = () => {
    setShowLogModal(false);
    setShowRawLog(false);
    setSelectedRow(null);
  };

  const renderSummaryWithCopyActions = (summaryText?: string | null) => {
    const text = summaryText || "Nenhum resumo disponível.";
    const summaryLines = text.split("\n");
    const translatedLabels = {
      provider: t(translationPath + "provider", {
        defaultValue: "Fornecedor",
      }),
      cnpj: t(translationPath + "cnpj", { defaultValue: "CNPJ" }),
      invoice: t(translationPath + "invoice", { defaultValue: "Nota" }),
      id: t(translationPath + "id", { defaultValue: "ID" }),
    };
    const copyTooltipLabel = t("common.copy", { defaultValue: "Copiar" });
    const copyableLabels = new Map<string, keyof typeof translatedLabels>([
      ["Fornecedor", "provider"],
      ["CNPJ", "cnpj"],
      ["Nota", "invoice"],
      ["ID", "id"],
      [translatedLabels.provider, "provider"],
      [translatedLabels.cnpj, "cnpj"],
      [translatedLabels.invoice, "invoice"],
      [translatedLabels.id, "id"],
    ]);
    const messageLines: string[] = [];

    const summaryContent = summaryLines.map((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        messageLines.push("");
        return null;
      }

      const separatorIndex = trimmedLine.indexOf(":");

      if (separatorIndex === -1) {
        messageLines.push(line);
        return null;
      }

      const label = trimmedLine.slice(0, separatorIndex).trim();
      const value = trimmedLine.slice(separatorIndex + 1).trim();
      const translationKey = copyableLabels.get(label);

      if (!translationKey) {
        messageLines.push(line);
        return null;
      }

      return (
        <Box
          key={`${label}-${index}`}
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {translatedLabels[translationKey]}:
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
            {value}
          </Typography>
          <Tooltip title={copyTooltipLabel}>
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(value.trim())}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    });

    const baseMessage = messageLines.join("\n").trim();

    return (
      <>
        {summaryContent}
        {baseMessage && (
          <Typography
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              mt: 2,
            }}
          >
            {baseMessage}
          </Typography>
        )}
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "log_id") || "ID do Log",
      width: 80,
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "success",
      headerName: t(translationPath + "status") || "Status",
      width: 100,
      renderCell: (params) => {
        return (
          <Chip
            label={
              params.value
                ? t(translationPath + "success") || "Sucesso"
                : t(translationPath + "error") || "Erro"
            }
            color={params.value ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "created",
      headerName: t(translationPath + "created") || "Data de Criação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "lastModified",
      headerName: t(translationPath + "last_modified") || "Última Modificação",
      width: 160,
      renderCell: (params) => {
        return formatDate(params.value);
      },
    },
    {
      field: "logData",
      headerName: t(translationPath + "summary") || "Resumo do Log",
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        const technicalLogData = params.row.logData || "";
        const displayText = params.row.summary || technicalLogData || "";
        const preview =
          displayText.length > 150
            ? displayText.substring(0, 150) + "..."
            : displayText;

        const handleShowFullLog = () => {
          setSelectedRow(params.row);
          setShowLogModal(true);
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {preview}
            </Typography>
            {technicalLogData && (
              <Tooltip
                title={
                  t(translationPath + "view_full_log") || "Ver log completo"
                }
              >
                <IconButton
                  size="small"
                  onClick={handleShowFullLog}
                  sx={{ padding: 0.5 }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogActions>
          <Button onClick={onClose}>X</Button>
        </DialogActions>
        <DialogTitle sx={{ paddingY: 0, paddingX: 6, overflow: "hidden" }}>
          {t(translationPath + "title") || "Detalhes da NFe"} -{" "}
          {nfeData.identifier}
        </DialogTitle>
        <DialogContent
          sx={{ height: 500, width: "100%", marginTop: 0, overflow: "hidden" }}
        >
          <TableList
            columns={columns}
            rows={logData}
            fileName={`nfe-logs-${nfeData.identifier}`}
            showSelects={false}
            onRowSelectionModelChange={undefined}
            pageSizeOptions={[5, 10, 25, 50]}
            hideTitle={true}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={showLogModal}
        onClose={handleCloseLogModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t(translationPath + "log_data") || "Dados do Log"}
        </DialogTitle>
        <DialogContent>
          {renderSummaryWithCopyActions(
            selectedRow?.summary ||
              selectedRow?.logData ||
              "Nenhum resumo disponível.",
          )}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowRawLog((prev) => !prev)}
            >
              {showRawLog ? "Ocultar log completo" : "Ver log completo"}
            </Button>
          </Box>
          {showRawLog && (
            <Typography
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.8rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: (theme) => theme.palette.grey[100],
                padding: 2,
                borderRadius: 1,
                maxHeight: 400,
                overflow: "auto",
                mt: 2,
              }}
            >
              {selectedRow?.logData}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogModal}>
            {t(translationPath + "close") || "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NfeTotvsDetailsModal;
